import {
    ExpenseAreaForRender,
    QuestionAnswer,
    buildSaveDataFromExpenseArea
} from './supplierProfilePreviewQuestionaryLogic';
import { Section } from '../../../../api/types/expenseareas';
import type { ScoreCardEvaluationSubmitBody } from '../../../../api/tenders';

export * from './supplierProfilePreviewQuestionaryLogic';

const SCORE_CARD_PAYLOAD_FIELD_TYPES = new Set([
    'slider',
    'single-select',
    'multiple-choice',
    'dropdown',
    'short-answer',
    'paragraph'
]);

export function hasPreviewComponents(payload: any): boolean {
    return Array.isArray(payload?.components) && payload.components.length > 0;
}

export function listHasComponentsStep(area: ExpenseAreaForRender | undefined): boolean {
    return !!area?.sections?.some((s) => String(s.id).includes('components'));
}

export function applyScoreCardStatusByLastSection(data: any, goToSections: number[]): ExpenseAreaForRender[] {
    const list = Array.isArray(data) ? data : [];

    return list.map((elem: any) => {
        const lastSectionId = elem.progressData?.last_section_id ?? null;
        const sections = elem.formData?.sections ?? [];

        const formSections = sections
            .filter((section: Section) => {
                if (!section.has_go_to) return true;
                if (section.has_go_to && goToSections.includes(section.order)) return true;
                return false;
            })
            .map((section: Section) => ({
                name: section.title,
                id: section.id + '',
                order: section.order,
                description: section.description
            }));

        const sidebarSections = hasPreviewComponents(elem)
            ? [
                  {
                      id: `components-${elem.expense_area_id}`,
                      name: 'Service Components',
                      order: null
                  },
                  ...formSections
              ]
            : formSections;

        return {
            name: elem.expense_area_name ?? elem.name ?? '',
            expense_area_name: elem.expense_area_name ?? elem.name ?? '',
            description: elem.description,
            expense_area_id: elem.expense_area_id,
            last_section_id: lastSectionId,
            order: elem.order ?? 0,
            selected_components: elem.selected_components ?? [],
            sections: sidebarSections
        };
    });
}

export function getScoreCardInitialSectionId(payload: any): string {
    if (hasPreviewComponents(payload)) {
        return `components-${payload.expense_area_id}`;
    }

    const sections = payload?.formData?.sections ?? [];
    const first = sections.find((s: any) => !s?.has_go_to) ?? sections[0];
    return first ? String(first.id) : '';
}

export function goToPrevScoreCardSectionState(
    list: ExpenseAreaForRender[],
    activeEA: string,
    activeSectionId: string
): { activeEA: string; activeSectionId: string } | null {
    const currentEAIndex = list?.findIndex((elem) => elem.expense_area_id + '' === activeEA + '');
    const currentArea = list[currentEAIndex];
    const sections = currentArea?.sections;
    const prevAreaIndex = currentEAIndex === 0 ? 0 : currentEAIndex - 1;
    const prevAreaSections = list[prevAreaIndex]?.sections ?? [];
    const lastSection = prevAreaSections[prevAreaSections.length - 1];

    if (activeSectionId.includes('components')) {
        if (currentEAIndex === 0) return null;
        return {
            activeEA: list[prevAreaIndex]?.expense_area_id + '',
            activeSectionId: lastSection?.id + ''
        };
    }

    const currentIndex = sections?.findIndex((s) => s.id + '' === activeSectionId + '') ?? -1;

    if (currentEAIndex === 0 && currentIndex <= 0) return null;

    if (currentIndex > 0) {
        const prevSec = sections[currentIndex - 1];
        return { activeEA, activeSectionId: prevSec.id + '' };
    }

    if (currentIndex === 0 && listHasComponentsStep(currentArea)) {
        return {
            activeEA,
            activeSectionId: `components-${currentArea.expense_area_id}`
        };
    }

    if (currentIndex === 0 && currentEAIndex > 0) {
        return {
            activeEA: list[prevAreaIndex]?.expense_area_id + '',
            activeSectionId: lastSection?.id + ''
        };
    }

    return null;
}

export function goToNextScoreCardSectionState(
    list: ExpenseAreaForRender[],
    activeEA: string,
    activeSectionId: string
): { activeEA: string; activeSectionId: string } | null {
    const currentAreaIndex = list.findIndex((elem) => elem.expense_area_id + '' === activeEA + '');
    const currentArea = list[currentAreaIndex];
    const sections = currentArea?.sections ?? [];

    const currentIndex = sections.findIndex((s) => s.id + '' === activeSectionId + '');
    const isLastSection = currentIndex === sections.length - 1;

    if (activeSectionId.includes('components') && sections.length > 1) {
        return { activeEA, activeSectionId: sections[1].id + '' };
    }

    if (!isLastSection && currentIndex >= 0) {
        return { activeEA, activeSectionId: sections[currentIndex + 1].id + '' };
    }

    const nextArea = list[currentAreaIndex + 1];
    if (nextArea) {
        const firstSectionId = listHasComponentsStep(nextArea)
            ? `components-${nextArea.expense_area_id}`
            : nextArea.sections[0]?.id + '';
        return {
            activeEA: nextArea.expense_area_id + '',
            activeSectionId: firstSectionId
        };
    }

    return null;
}

function isRequiredFromSettings(field: any): boolean {
    return !!field.settings?.find((s: any) => s.type === 'required')?.switchValue;
}

export function hasComponentScope(componentId: unknown): boolean {
    if (componentId == null) return false;
    const s = String(componentId).trim();
    return s !== '' && s !== 'undefined';
}

export function expandScorecardSectionFields(fields: any[] | undefined): any[] {
    const list = fields ?? [];
    const result: any[] = [];

    for (const field of list) {
        const perService = field.settings?.find((s: any) => s.type === 'per_service_component');
        const alreadyScoped = hasComponentScope(field.component_id);

        if (field.type === 'slider' && perService?.switchValue && !alreadyScoped) {
            const selected = (perService.options ?? []).filter((o: any) => o.selected);
            if (selected.length === 0) {
                result.push(field);
            } else {
                for (const opt of selected) {
                    result.push({
                        ...field,
                        component_id: opt.value,
                        component_name: opt.label ?? opt.name
                    });
                }
            }
        } else {
            result.push(field);
        }
    }

    return result;
}

function apiFieldToQuestionAnswer(field: any): QuestionAnswer {
    const isListType = field.type === 'multiple-choice' || field.type === 'dropdown';
    const initialAnswer = isListType
        ? Array.isArray(field.answer)
            ? field.answer
            : field.answer
              ? [field.answer]
              : []
        : (field.answer ?? '');

    const desc =
        field.type === 'explanation-banner' ? (field.description ?? field.label ?? '') : field.description;

    const required =
        field.type === 'explanation-banner'
            ? false
            : field.type === 'slider'
              ? isRequiredFromSettings(field)
              : isRequiredFromSettings(field);

    return {
        id: +field.id,
        answer: initialAnswer,
        required,
        error: false,
        component_id: field.component_id ?? null,
        type: field.type,
        name: field.label ?? field.title ?? '',
        description: desc,
        options: field.options,
        component_name: field.component_name
    };
}

function isScoreFieldAnswerEmpty(item: QuestionAnswer): boolean {
    if (item.type === 'slider' || item.type === 'single-select') {
        return item.answer === '' || item.answer == null;
    }
    if (Array.isArray(item.answer)) return item.answer.length === 0;
    return !item.answer;
}

export function findSaveRowForApiField(saveData: QuestionAnswer[], apiField: any): QuestionAnswer | undefined {
    return saveData.find((elem) => {
        const matchById = elem.id === apiField.id;
        if (hasComponentScope(apiField.component_id)) {
            return matchById && String(elem.component_id) === String(apiField.component_id);
        }
        return matchById;
    });
}

export function saveRowMatchesExpandedField(item: QuestionAnswer, apiField: any): boolean {
    if (item.id !== apiField.id) return false;
    if (hasComponentScope(apiField.component_id)) {
        return String(item.component_id) === String(apiField.component_id);
    }
    return item.component_id == null;
}

export function buildFlatScoreCardSaveData(sections: any[]): QuestionAnswer[] {
    return sections.flatMap((section) =>
        expandScorecardSectionFields(section.fields).map(apiFieldToQuestionAnswer)
    );
}

export function resolveScoreCardActiveSectionId(payload: any, sections: any[]): string {
    if (hasPreviewComponents(payload)) {
        const pid = payload?.progressData?.last_section_id;
        if (pid == null) {
            return `components-${payload.expense_area_id}`;
        }
    }

    const pid = payload?.progressData?.last_section_id ?? payload?.progressData?.order_section_id;
    if (pid != null && sections.some((s) => String(s.id) === String(pid))) {
        return String(pid);
    }

    return sections[0] ? String(sections[0].id) : '';
}

export function validateCurrentScoreCardSection(
    saveData: QuestionAnswer[],
    activeSection: any | null
): { updated: QuestionAnswer[]; isValid: boolean } {
    const expanded = expandScorecardSectionFields(activeSection?.fields);
    let isValid = true;

    const updated = saveData.map((item) => {
        const inSection = expanded.some((f) => saveRowMatchesExpandedField(item, f));
        if (!inSection || !item.required) return item;

        const hasError = isScoreFieldAnswerEmpty(item);
        if (hasError) isValid = false;

        return {
            ...item,
            error: hasError,
            errorMessage: hasError ? undefined : item.errorMessage
        };
    });

    return { updated, isValid };
}

export function getCurrentSectionFieldsForApi(saveData: QuestionAnswer[], activeSection: any | null) {
    const expanded = expandScorecardSectionFields(activeSection?.fields);

    return expanded
        .map((apiField) => findSaveRowForApiField(saveData, apiField))
        .filter((row): row is QuestionAnswer => !!row)
        .map((item) => {
            const { required, error, errorMessage, ...rest } = item;
            return rest;
        });
}

function normalizeAnswerForScoreCardPayload(
    raw: QuestionAnswer['answer'] | undefined | null,
    type: string
): string | number | boolean | string[] | null {
    if (raw === undefined || raw === null) return null;
    if (type === 'multiple-choice' || type === 'dropdown') {
        if (Array.isArray(raw)) return raw.length ? raw : null;
        const s = String(raw);
        return s ? [s] : null;
    }
    if (raw === '') return null;
    if (typeof raw === 'number' || typeof raw === 'boolean') return raw;
    if (Array.isArray(raw)) return raw.length ? raw : null;
    return String(raw);
}

/** Same payload shape as real Score Card POST `/supplier-companies/evaluation/:token`. */
export function buildScoreCardPreviewSubmitBody(
    sectionList: any[],
    answers: QuestionAnswer[],
    userName: string
): ScoreCardEvaluationSubmitBody {
    return {
        user_name: userName,
        sections: sectionList.map((section) => ({
            id: section.id,
            fields: expandScorecardSectionFields(section.fields)
                .filter((f: any) => SCORE_CARD_PAYLOAD_FIELD_TYPES.has(f.type))
                .map((f: any) => {
                    const qa = findSaveRowForApiField(answers, f);
                    const raw = qa ? qa.answer : f.answer;
                    const entry: {
                        type: string;
                        id: number;
                        answer: ReturnType<typeof normalizeAnswerForScoreCardPayload>;
                        component_id?: number | string | null;
                    } = {
                        type: f.type,
                        id: f.id,
                        answer: normalizeAnswerForScoreCardPayload(raw, f.type)
                    };
                    if (hasComponentScope(f.component_id)) {
                        entry.component_id = f.component_id;
                    }
                    return entry;
                })
        }))
    };
}

export function validateAllScoreCardSections(
    sections: any[],
    saveData: QuestionAnswer[]
): { updated: QuestionAnswer[]; isValid: boolean } {
    const allExpanded = sections.flatMap((s) => expandScorecardSectionFields(s.fields));
    let isValid = true;

    const updated = saveData.map((item) => {
        const inForm = allExpanded.some((f) => saveRowMatchesExpandedField(item, f));
        if (!inForm || !item.required) return item;

        const hasError = isScoreFieldAnswerEmpty(item);
        if (hasError) isValid = false;

        return {
            ...item,
            error: hasError,
            errorMessage: hasError ? undefined : item.errorMessage
        };
    });

    return { updated, isValid };
}

export function buildScoreCardSaveDataFromExpenseArea(activeSectionId: string, expenseAreaData: any): QuestionAnswer[] {
    if (activeSectionId.includes('components')) {
        return buildSaveDataFromExpenseArea(activeSectionId, expenseAreaData);
    }

    const findSection = expenseAreaData?.formData?.sections?.find(
        (elem: any) => elem.id + '' === activeSectionId + ''
    );
    if (!findSection) return [];

    return expandScorecardSectionFields(findSection.fields).map(apiFieldToQuestionAnswer);
}

export function validateScoreCardAnswers(data: QuestionAnswer[]) {
    let isValid = true;

    const updated = data.map((item) => {
        if (!item || typeof item !== 'object') return item;

        const hasClientError = item.required && isScoreFieldAnswerEmpty(item);
        if (hasClientError) isValid = false;

        return {
            ...item,
            error: Boolean(hasClientError) || Boolean(item.errorMessage),
            errorMessage: hasClientError ? undefined : item.errorMessage
        };
    });

    return { updated, isValid };
}

export function cleanScoreCardFieldsForApi(saveData: QuestionAnswer[]) {
    return saveData.map((item) => {
        if (!item || typeof item !== 'object') return item;

        const { required, error, errorMessage, ...rest } = item as QuestionAnswer & {
            required?: boolean;
            error?: boolean;
            errorMessage?: string;
        };

        if (rest.type === 'dropdown') {
            const ans = rest.answer;
            const answerForApi =
                ans === '' || ans == null
                    ? []
                    : Array.isArray(ans)
                      ? ans.filter((v: any) => v != null && v !== '')
                      : [ans];
            return { ...rest, answer: answerForApi };
        }

        return rest;
    });
}
